import React, { useState, useRef, useEffect } from "react";
import { Input } from "../../components/ui/input";
import { X } from "lucide-react";
import Header from "../../layouts/Header";
import { useNavigate } from "react-router-dom";
import wtsappImage from "../../assets/images/support/whatsapp.png";
import sendImage from "../../assets/images/support/send.png";
import { get, create } from "../../services/apiService";

const SUPPORT_CHAT_GET = "api/v1/support/chat";
const SUPPORT_CHAT_POST = "api/v1/support/chat";

const TYPEWRITER_CHAR_MS = 28;

const TypingIndicator = () => (
  <div className="flex justify-start">
    <div className="min-w-[258px] max-w-[70%] bg-white [clip-path:inset(0_round_0px_28px_28px_35px)] shadow-lg lg:pb-5 pb-5 h-[56px] px-4 pt-4 flex items-center justify-start gap-1">
      <span className="flex gap-1" aria-label="Support is typing">
        <span
          className="w-2 h-2 rounded-full bg-[#6D6584] animate-typing-bounce"
          style={{ animationDelay: "0s" }}
        />
        <span
          className="w-2 h-2 rounded-full bg-[#6D6584] animate-typing-bounce"
          style={{ animationDelay: "0.2s" }}
        />
        <span
          className="w-2 h-2 rounded-full bg-[#6D6584] animate-typing-bounce"
          style={{ animationDelay: "0.4s" }}
        />
      </span>
    </div>
  </div>
);

const TypewriterBubble = ({ fullText, onComplete }) => {
  const [visibleLength, setVisibleLength] = useState(0);
  const completedRef = useRef(false);
  const isComplete = visibleLength >= fullText.length;

  useEffect(() => {
    if (visibleLength >= fullText.length) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      return;
    }
    const t = setTimeout(() => {
      setVisibleLength((prev) => Math.min(prev + 1, fullText.length));
    }, TYPEWRITER_CHAR_MS);
    return () => clearTimeout(t);
  }, [visibleLength, fullText.length, onComplete]);

  return (
    <div className="flex justify-start">
      <div className="min-w-[258px] max-w-[70%] bg-white [clip-path:inset(0_round_0px_28px_28px_35px)] shadow-lg lg:pb-5 pb-5 min-h-[56px] px-4 pt-4 flex items-center justify-start">
        <p className="text-[14px] text-[#33125F]">
          {fullText.slice(0, visibleLength)}
          {!isComplete && (
            <span
              className="inline-block w-[2px] h-4 ml-0.5 align-middle bg-[#33125F] animate-cursor-blink"
              aria-hidden
            />
          )}
        </p>
      </div>
    </div>
  );
};

const normalizeMessage = (item, index) => {
  const id = item.id ?? index + 1;
  const text = item.text ?? item.message ?? item.content ?? "";
  const type = (
    item.type ??
    item.role ??
    item.sender ??
    "support"
  ).toLowerCase();
  return {
    id,
    text,
    type: type === "user" || type === "customer" ? "user" : "support",
  };
};

const SupportChat = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleTypewriterComplete = (messageId) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId && m.typingOut ? { ...m, typingOut: false } : m
      )
    );
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchChat = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await get(SUPPORT_CHAT_GET);
        const data = response?.data?.data ?? response?.data;
        const list = Array.isArray(data)
          ? data
          : (data?.messages ?? data?.history ?? []);
        const normalized = (list || []).map(normalizeMessage);
        setMessages(
          normalized.length
            ? normalized
            : [{ id: 1, text: "Hello, how can we help?", type: "support" }]
        );
      } catch (err) {
        setError(err?.response?.data?.message ?? "Failed to load chat.");
        setMessages([
          { id: 1, text: "Hello, how can we help?", type: "support" },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchChat();
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || sending) return;

    const userMessage = {
      id: Date.now(),
      text: trimmed,
      type: "user",
    };
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setSending(true);
    setError(null);

    try {
      const response = await create(SUPPORT_CHAT_POST, { message: trimmed });
      const data = response?.data?.data ?? response?.data;
      const reply = data?.response;
      if (reply && typeof reply === "string") {
        const supportMessage = {
          id: data?.messageId ?? Date.now() + 1,
          text: reply,
          type: "support",
          typingOut: true,
          fullText: reply,
        };
        setMessages((prev) => [...prev, supportMessage]);
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen w-full flex bg-[#F8F8F8] flex-col items-center justify-center px-4 py-6">
        <div className="max-w-[844px] w-full rounded-2xl relative">
          <button
            onClick={() => navigate("/support/index")}
            className="ml-auto w-[48px] h-[48px] rounded-full flex items-center justify-center border-[1px] border-[#EBE9EF] transition-colors z-10 mb-8"
          >
            <X size={23} className="text-[#6D6584]" />
          </button>

          <div className="text-center lg:mb-14 mb-6">
            <h1 className="lg:text-[32px] text-[20px] font-semibold text-[#33125F]">
              Hello, how can we help?
            </h1>
          </div>

          {error && (
            <div className="mb-4 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="pb-4 space-y-16 h-[55vh] mb-5 overflow-y-auto chat-messages lg:max-w-[944px] max-w-[844px] w-full lg:pr-[100px] lg:ml-[100px]">
          {loading ? (
            <div className="flex justify-center items-center h-full text-[#6D6584] text-sm">
              Loading chat...
            </div>
          ) : (
            <>
              {messages.map((msg) => {
                if (msg.type === "support" && msg.typingOut && msg.fullText) {
                  return (
                    <TypewriterBubble
                      key={msg.id}
                      fullText={msg.fullText}
                      onComplete={() => handleTypewriterComplete(msg.id)}
                    />
                  );
                }
                return (
                  <div
                    key={msg.id}
                    className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] ${
                        msg.type === "user"
                          ? "min-w-[239px] bg-[linear-gradient(86.72deg,#EB3366_0%,#57249C_100%)] [clip-path:inset(0_round_28px_0_32px_28px)] lg:pb-4 pb-4"
                          : "min-w-[258px] bg-white [clip-path:inset(0_round_0px_28px_28px_35px)] shadow-lg  lg:pb-5 pb-5"
                      } min-h-[56px] px-4 pt-4 flex items-center justify-start`}
                    >
                      <p
                        className={`text-[14px] ${
                          msg.type === "user" ? "text-white" : "text-[#33125F]"
                        }`}
                      >
                        {msg.text}
                      </p>
                    </div>
                  </div>
                );
              })}
              {sending && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="max-w-[844px] w-full">
          <div className="pb-4">
            <form
              onSubmit={handleSendMessage}
              className="flex items-center gap-2"
            >
              <Input
                type="text"
                placeholder="Type something..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="flex-1 h-[56px] rounded-full text-[#6D6584] text-[14px] border border-[#EBE9EF] focus:ring focus:ring-brand-light pl-4 pr-4"
              />
              <button
                type="submit"
                disabled={sending || loading}
                className="bg-transparent border-none outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src={sendImage} alt="Send" className="w-[62px] h-[56px]" />
              </button>
            </form>
          </div>

          <div className="px-6 pb-6 flex justify-center mt-12">
            <a
              href="https://wa.me/+27664000888"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-[48px] px-4 rounded-full border border-[#EBE9EF] text-[#6D6584] font-semibold text-[14px] transition-colors"
            >
              <span>Continue on WhatsApp</span>
              <img
                src={wtsappImage}
                alt="WhatsApp"
                className="w-[20px] h-[20px]"
              />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportChat;
